import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Alert,
  Button,
  Card,
  Collapse,
  Drawer,
  Empty,
  Input,
  Popconfirm,
  Progress,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd'
import type { UploadProps } from 'antd'
import {
  CloudUploadOutlined,
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileOutlined,
  FileSearchOutlined,
  FolderOpenOutlined,
  ReloadOutlined,
  ScanOutlined,
} from '@ant-design/icons'
import { api } from '../api'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import { KNOWLEDGE_SCOPE_META } from '../constants/knowledgeScope'
import { PageHeader, PageLoading, formatDate, tablePagination, useServerPagination } from '../components/ui'
import { DedupDocTitle } from '../components/dedup/DedupMatchViewer'
import { openDedupMatchWindow } from '../components/dedup/dedupFormat'
import './FileDedup.css'

const { Text } = Typography

const POLL_MS = 2000

const DEDUP_ACCEPT = '.pdf,.doc,.docx,.rtf,application/pdf'
const DEDUP_FORMAT_HINT = '文件查重仅支持 PDF、Word（.doc / .docx / .rtf）'
const DEDUP_ALLOWED_EXT = new Set(['.pdf', '.doc', '.docx', '.rtf'])

function isDedupAllowedFile(name: string): boolean {
  const i = name.lastIndexOf('.')
  if (i < 0) return false
  return DEDUP_ALLOWED_EXT.has(name.slice(i).toLowerCase())
}

function resolveReportOriginalName(row: DedupReportMeta): string | undefined {
  const pick = (v?: string | null) => {
    const s = (v || '').trim()
    return s || undefined
  }
  const fromParams =
    row.params && typeof row.params === 'object' && 'query_original_name' in row.params
      ? (row.params as { query_original_name?: string }).query_original_name
      : undefined
  const fromResult =
    row.result && typeof row.result === 'object' && 'query_original_name' in row.result
      ? (row.result as { query_original_name?: string }).query_original_name
      : undefined
  return (
    pick(row.query_original_name) ||
    pick(row.summary?.query_original_name) ||
    pick(fromParams) ||
    pick(fromResult)
  )
}

function renderReportQueryTitle(row: DedupReportMeta) {
  return (
    <DedupDocTitle
      title={row.query_title || '—'}
      originalName={resolveReportOriginalName(row)}
    />
  )
}

type FileRec = {
  item_id: number
  title: string
  scope: string
  folder_id: number | null
  original_name: string
  stored_name: string
  size: number
  mime: string
}

type ContentRec = {
  item_id: number
  title: string
  scope: string
  folder_id: number | null
  content_preview: string
  original_name?: string
  updated_at: string | null
}

type FileGroup = {
  hash: string
  size: number
  count: number
  files: FileRec[]
}

type ContentGroup = {
  hash: string
  count: number
  items: ContentRec[]
}

type ScanResult = {
  scope: string
  mode: string
  scanned_items: number
  scanned_files: number
  missing_files: number
  file_duplicate_groups: number
  content_duplicate_groups: number
  file_groups: FileGroup[]
  content_groups: ContentGroup[]
  response_ms: number
}

type ExactUploadResult = {
  scope: string
  mode: string
  query_source: 'upload'
  query_item_id: number
  query_title: string
  query_original_name?: string
  file_matches: ExactFileMatchItem[]
  content_matches: ExactMatchItem[]
  file_duplicate_count: number
  content_duplicate_count: number
  has_duplicate: boolean
  response_ms: number
}

type DedupItem = {
  id: number
  title: string
  scope: string
  folder_id: number | null
  index_status: string
  chunk_count: number
  original_name?: string
}

type MatchBlock = {
  query_start: number
  query_end: number
  query_text: string
  source_start: number
  source_end: number
  source_text: string
  match_chars: number
}

type SourceBreakdown = {
  item_id: number
  title: string
  original_name?: string
  scope: string
  folder_id: number | null
  folder_name?: string
  duplicate_rate: number
  matched_chars: number
  match_count: number
  matches?: MatchBlock[]
}

type ExactMatchItem = {
  item_id: number
  title: string
  scope: string
  folder_id: number | null
  original_name?: string
}

type ExactFileMatchItem = ExactMatchItem & {
  original_name: string
}

type SimilarityResult = {
  query_item_id: number | null
  query_title: string
  query_original_name?: string
  query_scope: string
  query_source?: string
  scope: string
  total_chars: number
  matched_chars: number
  duplicate_rate: number
  min_match_chars: number
  corpus_items: number
  candidate_count: number
  source_count: number
  source_breakdown: SourceBreakdown[]
  response_ms: number
  exact_content_matches?: ExactMatchItem[]
  exact_file_matches?: ExactFileMatchItem[]
}

type ReindexResult = {
  mysql_indexed?: number
  es_fingerprints?: number
  total?: number
  skipped?: number
  failed?: number
  response_ms?: number
}

type DedupReportMeta = {
  id: number
  report_type: 'similarity' | 'exact_scan' | 'dedup_reindex'
  status: 'pending' | 'running' | 'completed' | 'failed'
  progress: number
  status_message: string
  query_item_id: number | null
  query_title: string
  query_original_name?: string | null
  duplicate_rate: number | null
  params?: Record<string, unknown> | null
  summary?: {
    duplicate_rate?: number
    source_count?: number
    query_original_name?: string
    file_duplicate_groups?: number
    content_duplicate_groups?: number
    file_duplicate_count?: number
    content_duplicate_count?: number
    mysql_indexed?: number
    es_fingerprints?: number
    total?: number
    skipped?: number
  } | null
  error_message?: string | null
  created_at: string | null
  finished_at: string | null
  result?: SimilarityResult | ScanResult | ExactUploadResult | ReindexResult
}

type ScopeOpt = 'all' | KnowledgeScope
type ModeOpt = 'both' | 'file' | 'content'

const STATUS_LABEL: Record<string, string> = {
  pending: '排队中',
  running: '进行中',
  completed: '已完成',
  failed: '失败',
}

type DedupReportType = DedupReportMeta['report_type']

const REPORT_TYPE_LABEL: Record<DedupReportType, string> = {
  similarity: '相似查重',
  exact_scan: '精确查重',
  dedup_reindex: '索引回填',
}

function reportTypeLabel(type: string) {
  return REPORT_TYPE_LABEL[type as DedupReportType] ?? type
}

function formatBytes(n: number) {
  if (!n) return '0 B'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function scopeTag(scope: string) {
  if (scope === 'upload') {
    return <Tag color="blue">本地上传</Tag>
  }
  const meta = KNOWLEDGE_SCOPE_META[scope as KnowledgeScope]
  const color = scope === 'enterprise' ? 'purple' : scope === 'personal' ? 'green' : 'default'
  return <Tag color={color}>{meta?.title ?? scope}</Tag>
}

function isExactUploadResult(res: ScanResult | ExactUploadResult | ReindexResult): res is ExactUploadResult {
  return (res as ExactUploadResult).query_source === 'upload'
}

function DedupReportDetailBody({ detail }: { detail: DedupReportMeta }) {
  const res = detail.result
  if (!res) return null
  if (detail.report_type === 'similarity') {
    return <SimilarityReportBody res={res as SimilarityResult} reportId={detail.id} />
  }
  if (detail.report_type === 'exact_scan') {
    if (isExactUploadResult(res)) {
      return <ExactUploadReportBody res={res} />
    }
    return <ExactScanReportBody res={res as ScanResult} />
  }
  if (detail.report_type === 'dedup_reindex') {
    return <ReindexReportBody res={res as ReindexResult} />
  }
  return <Alert type="warning" showIcon title="未知报告类型" />
}

function DedupSourceTabs<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (v: T) => void
}) {
  return (
    <div className="dedup-source-tabs">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`dedup-source-tab${value === opt.value ? ' dedup-source-tab--active' : ''}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function DedupHero({
  title,
  description,
  icon,
}: {
  title: string
  description: string
  icon: ReactNode
}) {
  return (
    <div className="dedup-hero">
      <div className="dedup-hero-icon">{icon}</div>
      <div className="dedup-hero-text">
        <span className="dedup-hero-title">{title}</span>
        <span className="dedup-hero-desc">{description}</span>
      </div>
    </div>
  )
}

function DedupFileSlot({
  file,
  onFileChange,
}: {
  file: File | null
  onFileChange: (f: File | null) => void
}) {
  const pickFile = (f: File) => {
    if (!isDedupAllowedFile(f.name)) {
      message.error(DEDUP_FORMAT_HINT)
      return
    }
    onFileChange(f)
  }

  const uploadProps: UploadProps = {
    maxCount: 1,
    accept: DEDUP_ACCEPT,
    showUploadList: false,
    beforeUpload: (f) => {
      pickFile(f)
      return false
    },
  }

  if (file) {
    return (
      <div className="dedup-file-card">
        <div className="dedup-file-card-icon">
          <FileOutlined />
        </div>
        <div className="dedup-file-card-meta">
          <span className="dedup-file-card-name" title={file.name}>
            {file.name}
          </span>
          <span className="dedup-file-card-sub">{formatBytes(file.size)}</span>
        </div>
        <div className="dedup-file-card-actions">
          <Upload {...uploadProps}>
            <Button size="small" icon={<ReloadOutlined />}>
              重新选择
            </Button>
          </Upload>
          <Button size="small" type="link" onClick={() => onFileChange(null)}>
            清除
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Upload.Dragger {...uploadProps} className="dedup-upload-dragger">
      <p className="ant-upload-drag-icon">
        <CloudUploadOutlined />
      </p>
      <p className="ant-upload-text">点击或拖拽 PDF / Word 到此处</p>
      <p className="ant-upload-hint">支持 .pdf · .doc · .docx · .rtf</p>
    </Upload.Dragger>
  )
}

function DedupLibrarySlot({
  selected,
  onBrowse,
}: {
  selected: { title: string; scope: string; original_name?: string } | null
  onBrowse: () => void
}) {
  if (selected) {
    const displayName = selected.original_name || selected.title
    return (
      <div className="dedup-file-card">
        <div className="dedup-file-card-icon">
          <FileOutlined />
        </div>
        <div className="dedup-file-card-meta">
          <span className="dedup-file-card-name" title={displayName}>
            {displayName}
          </span>
          <span className="dedup-file-card-sub">
            {KNOWLEDGE_SCOPE_META[selected.scope as KnowledgeScope]?.title ?? selected.scope}
          </span>
        </div>
        <div className="dedup-file-card-actions">
          <Button size="small" icon={<ReloadOutlined />} onClick={onBrowse}>
            重新选择
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div
      className="dedup-pick-zone"
      role="button"
      tabIndex={0}
      onClick={onBrowse}
      onKeyDown={(e) => e.key === 'Enter' && onBrowse()}
    >
      <div className="dedup-pick-icon-wrap">
        <FolderOpenOutlined />
      </div>
      <span className="dedup-pick-label">从知识库选择文档</span>
      <span className="dedup-pick-hint">仅列出 PDF / Word 附件</span>
    </div>
  )
}

function DedupActionBar({
  ready,
  loading,
  label,
  icon,
  onSubmit,
}: {
  ready: boolean
  loading: boolean
  label: string
  icon: ReactNode
  onSubmit: () => void
}) {
  return (
    <div className="dedup-action-bar">
      <Button type="primary" className="dedup-run-btn" icon={icon} loading={loading} disabled={!ready} onClick={onSubmit}>
        {label}
      </Button>
    </div>
  )
}

function DedupSteps({ steps }: { steps: string[] }) {
  return (
    <div className="dedup-steps">
      {steps.map((text, idx) => (
        <div className="dedup-step" key={text}>
          <span className="dedup-step-num">{idx + 1}</span>
          <span>{text}</span>
        </div>
      ))}
    </div>
  )
}

function DedupResultSection({
  reportId,
  pollMeta,
  children,
}: {
  reportId: number | null
  pollMeta: DedupReportMeta | null
  children: ReactNode
}) {
  if (!reportId) {
    return null
  }

  if (pollMeta && (pollMeta.status === 'pending' || pollMeta.status === 'running')) {
    return <ReportProgressCard meta={pollMeta} />
  }

  if (pollMeta?.status === 'failed') {
    return (
      <Alert
        type="error"
        showIcon
        title="查重失败"
        description={pollMeta.error_message || '任务执行异常，请稍后重试'}
        className="dedup-result-alert"
      />
    )
  }

  if (pollMeta?.status === 'completed') {
    return <div className="dedup-result-body">{children}</div>
  }

  return <PageLoading tip="正在加载报告…" />
}

function statusTag(status: string) {
  const color =
    status === 'completed' ? 'success' : status === 'failed' ? 'error' : status === 'running' ? 'processing' : 'default'
  return <Tag color={color}>{STATUS_LABEL[status] ?? status}</Tag>
}

function SourceBreakdownPanel({
  reportId,
  breakdown,
}: {
  reportId: number
  breakdown: SourceBreakdown[]
}) {
  const { page, handlePaginationChange, paginationOptions, resetPage } = useServerPagination()
  const [q, setQ] = useState('')

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase()
    if (!term) return breakdown
    return breakdown.filter(
      (s) =>
        s.title.toLowerCase().includes(term) ||
        (s.original_name || '').toLowerCase().includes(term),
    )
  }, [breakdown, q])

  useEffect(() => {
    resetPage()
  }, [q, breakdown.length, resetPage])

  const summaryColumns = useMemo(
    () => [
      {
        title: '来源文档',
        dataIndex: 'title',
        key: 'title',
        ellipsis: true,
        render: (_: string, row: SourceBreakdown) => (
          <DedupDocTitle title={row.title} originalName={row.original_name} />
        ),
      },
      {
        title: '范围',
        dataIndex: 'scope',
        key: 'scope',
        width: 110,
        render: (v: string) => scopeTag(v),
      },
      {
        title: '所属文件夹',
        dataIndex: 'folder_name',
        key: 'folder_name',
        ellipsis: true,
        render: (v: string) => v || '—',
      },
      {
        title: '重复率',
        dataIndex: 'duplicate_rate',
        key: 'duplicate_rate',
        width: 100,
        render: (v: number) => (
          <Tag color={v > 30 ? 'red' : v > 10 ? 'orange' : 'gold'}>{v.toFixed(2)}%</Tag>
        ),
      },
      {
        title: '重复字数',
        dataIndex: 'matched_chars',
        key: 'matched_chars',
        width: 96,
      },
      {
        title: '匹配处',
        dataIndex: 'match_count',
        key: 'match_count',
        width: 80,
      },
      {
        title: '操作',
        key: 'action',
        width: 96,
        render: (_: unknown, row: SourceBreakdown) => (
          <Button
            type="link"
            size="small"
            onClick={() => openDedupMatchWindow(reportId, row.item_id)}
          >
            查看片段
          </Button>
        ),
      },
    ],
    [reportId],
  )

  return (
    <section className="dedup-section">
      <div className="dedup-section-head dedup-section-head--stack">
        <Text strong>相似来源（{breakdown.length} 篇）</Text>
        <Text type="secondary" className="dedup-section-desc">
          按重复率从高到低排列。点击「查看片段」将在新窗口打开对照页（页码与字符位置）。
        </Text>
      </div>

      {breakdown.length > 5 ? (
        <Input.Search
          allowClear
          placeholder="搜索来源文档标题或文件名"
          className="dedup-source-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      ) : null}

      <Table
        rowKey="item_id"
        size="small"
        className="dedup-source-summary-table"
        columns={summaryColumns}
        dataSource={filtered}
        locale={{ emptyText: q.trim() ? '无匹配来源' : '暂无相似来源' }}
        pagination={tablePagination(page, filtered.length, handlePaginationChange, paginationOptions)}
      />
    </section>
  )
}

function SimilaritySummaryCard({
  res,
  onOpenFull,
}: {
  res: SimilarityResult
  onOpenFull?: () => void
}) {
  return (
    <>
      <Card className="dedup-report-summary" bordered={false}>
        <div className="dedup-report-head">
          <div>
            <Text type="secondary">待查文档</Text>
            <div>
              <DedupDocTitle title={res.query_title} originalName={res.query_original_name} />{' '}
              {scopeTag(res.query_scope)}
            </div>
          </div>
          <div className="dedup-rate-block">
            <div className="dedup-rate-big">{res.duplicate_rate.toFixed(2)}%</div>
            <Text type="secondary">总重复率</Text>
          </div>
        </div>
        <Progress
          percent={Math.min(100, res.duplicate_rate)}
          strokeColor={res.duplicate_rate > 30 ? '#ff4d4f' : '#faad14'}
          showInfo={false}
        />
        <div className="dedup-stats dedup-stats-report">
          <Statistic title="文档总字数" value={res.total_chars} />
          <Statistic title="相似来源" value={res.source_count} suffix={`/ ${res.corpus_items} 篇语料`} />
          <Statistic title="耗时" value={res.response_ms} suffix="ms" />
        </div>
      </Card>

      {res.duplicate_rate <= 0 &&
      !res.exact_content_matches?.length &&
      !res.exact_file_matches?.length ? (
        <Empty description="未发现达到阈值的重复片段" />
      ) : (
        <Alert
          type="info"
          showIcon
          className="dedup-inline-hint"
          title={
            res.source_count > 0
              ? `共检测到 ${res.source_count} 篇相似来源，重复来源明细与片段对照请打开完整报告查看。`
              : '精确重复项已列出；相似片段明细请打开完整报告查看。'
          }
          action={
            onOpenFull ? (
              <Button size="small" type="primary" onClick={onOpenFull}>
                打开完整报告
              </Button>
            ) : undefined
          }
        />
      )}
    </>
  )
}

function renderExactDocTitle(_: string, row: ExactMatchItem) {
  return <DedupDocTitle title={row.title} originalName={row.original_name} />
}

function SimilarityReportBody({ res, reportId }: { res: SimilarityResult; reportId: number }) {
  const exactColumns = useMemo(
    () => [
      {
        title: '文档',
        dataIndex: 'title',
        key: 'title',
        ellipsis: true,
        render: renderExactDocTitle,
      },
      { title: '范围', dataIndex: 'scope', key: 'scope', width: 110, render: (v: string) => scopeTag(v) },
    ],
    []
  )

  const exactFileColumns = useMemo(
    () => [
      ...exactColumns,
      { title: '文件名', dataIndex: 'original_name', key: 'original_name', ellipsis: true },
    ],
    [exactColumns]
  )

  return (
    <>
      <Card className="dedup-report-summary" bordered={false}>
        <div className="dedup-report-head">
          <div>
            <Text type="secondary">待查文档</Text>
            <div>
              <DedupDocTitle title={res.query_title} originalName={res.query_original_name} />{' '}
              {scopeTag(res.query_scope)}
            </div>
          </div>
          <div className="dedup-rate-block">
            <div className="dedup-rate-big">{res.duplicate_rate.toFixed(2)}%</div>
            <Text type="secondary">总重复率</Text>
          </div>
        </div>
        <Progress
          percent={Math.min(100, res.duplicate_rate)}
          strokeColor={res.duplicate_rate > 30 ? '#ff4d4f' : '#faad14'}
          showInfo={false}
        />
        <div className="dedup-stats dedup-stats-report">
          <Statistic title="文档总字数" value={res.total_chars} />
          <Statistic title="相似来源" value={res.source_count} suffix={`/ ${res.corpus_items} 篇语料`} />
          <Statistic title="耗时" value={res.response_ms} suffix="ms" />
        </div>
      </Card>

      {res.exact_content_matches?.length || res.exact_file_matches?.length ? (
        <section className="dedup-section">
          <div className="dedup-section-head">
            <CopyOutlined />
            <Text strong>精确重复（与库内完全一致）</Text>
          </div>
          {res.exact_content_matches?.length ? (
            <>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 8 }}
                title={`正文与 ${res.exact_content_matches.length} 篇知识库文档完全相同`}
              />
              <Table
                rowKey={(r) => `content-${r.item_id}`}
                size="small"
                pagination={false}
                columns={exactColumns}
                dataSource={res.exact_content_matches}
                style={{ marginBottom: 16 }}
              />
            </>
          ) : null}
          {res.exact_file_matches?.length ? (
            <>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 8 }}
                title={`附件文件与 ${res.exact_file_matches.length} 篇知识库文档完全相同`}
              />
              <Table
                rowKey={(r) => `file-${r.item_id}-${r.original_name}`}
                size="small"
                pagination={false}
                columns={exactFileColumns}
                dataSource={res.exact_file_matches}
              />
            </>
          ) : null}
        </section>
      ) : null}

      {res.duplicate_rate <= 0 ? (
        res.exact_content_matches?.length || res.exact_file_matches?.length ? null : (
          <Empty description="未发现达到阈值的重复片段" />
        )
      ) : (
        <SourceBreakdownPanel reportId={reportId} breakdown={res.source_breakdown ?? []} />
      )}
    </>
  )
}

function ExactUploadReportBody({ res }: { res: ExactUploadResult }) {
  const matchColumns = useMemo(
    () => [
      {
        title: '文档',
        dataIndex: 'title',
        key: 'title',
        ellipsis: true,
        render: renderExactDocTitle,
      },
      { title: '范围', dataIndex: 'scope', key: 'scope', width: 110, render: (v: string) => scopeTag(v) },
    ],
    []
  )

  const fileColumns = useMemo(
    () => [
      ...matchColumns,
      { title: '文件名', dataIndex: 'original_name', key: 'original_name', ellipsis: true },
    ],
    [matchColumns]
  )

  const showFile = res.mode === 'file' || res.mode === 'both'
  const showContent = res.mode === 'content' || res.mode === 'both'

  return (
    <>
      <Card className="dedup-report-summary" bordered={false}>
        <div className="dedup-report-head">
          <div>
            <Text type="secondary">待查文档</Text>
            <div>
              <DedupDocTitle title={res.query_title} originalName={res.query_original_name} />{' '}
              {scopeTag('upload')}
            </div>
          </div>
        </div>
        <div className="dedup-stats dedup-stats-report">
          {showFile ? <Statistic title="附件重复" value={res.file_duplicate_count} suffix="篇" /> : null}
          {showContent ? <Statistic title="正文重复" value={res.content_duplicate_count} suffix="篇" /> : null}
          <Statistic title="耗时" value={res.response_ms} suffix="ms" />
        </div>
      </Card>

      {!res.has_duplicate ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="未发现与知识库完全相同的附件或正文。" />
      ) : null}

      {showFile && res.file_matches.length > 0 ? (
        <section className="dedup-section">
          <div className="dedup-section-head">
            <CopyOutlined />
            <Text strong>附件完全相同（{res.file_matches.length} 篇）</Text>
          </div>
          <Table
            rowKey={(r) => `file-${r.item_id}-${r.original_name}`}
            size="small"
            pagination={false}
            columns={fileColumns}
            dataSource={res.file_matches}
          />
        </section>
      ) : null}

      {showContent && res.content_matches.length > 0 ? (
        <section className="dedup-section">
          <div className="dedup-section-head">
            <CopyOutlined />
            <Text strong>正文完全相同（{res.content_matches.length} 篇）</Text>
          </div>
          <Table
            rowKey={(r) => `content-${r.item_id}`}
            size="small"
            pagination={false}
            columns={matchColumns}
            dataSource={res.content_matches}
          />
        </section>
      ) : null}
    </>
  )
}

function ReindexReportBody({ res }: { res: ReindexResult }) {
  return (
    <div className="dedup-stats dedup-stats-report">
      <Card size="small">
        <Statistic title="总条目" value={res.total ?? 0} />
      </Card>
      <Card size="small">
        <Statistic title="MySQL 哈希" value={res.mysql_indexed ?? 0} />
      </Card>
      <Card size="small">
        <Statistic title="ES 指纹" value={res.es_fingerprints ?? 0} />
      </Card>
      <Card size="small">
        <Statistic title="跳过" value={res.skipped ?? 0} />
      </Card>
      <Card size="small">
        <Statistic title="耗时" value={res.response_ms ?? 0} suffix="ms" />
      </Card>
    </div>
  )
}

function ExactScanReportBody({ res }: { res: ScanResult }) {
  const fileGroups = res.file_groups ?? []
  const contentGroups = res.content_groups ?? []
  const missingFiles = res.missing_files ?? 0
  const fileColumns = useMemo(
    () => [
      {
        title: '条目',
        dataIndex: 'title',
        key: 'title',
        render: (_: string, row: FileRec) => (
          <Space direction="vertical" size={2}>
            <DedupDocTitle title={row.title} originalName={row.original_name} />
            <Text type="secondary">ID {row.item_id}</Text>
          </Space>
        ),
      },
      { title: '范围', dataIndex: 'scope', key: 'scope', width: 110, render: (v: string) => scopeTag(v) },
      { title: '文件名', dataIndex: 'original_name', key: 'original_name', ellipsis: true },
      { title: '大小', dataIndex: 'size', key: 'size', width: 100, render: (v: number) => formatBytes(v) },
    ],
    []
  )

  const contentColumns = useMemo(
    () => [
      {
        title: '条目',
        dataIndex: 'title',
        key: 'title',
        render: (_: string, row: ContentRec) => (
          <Space direction="vertical" size={2}>
            <DedupDocTitle title={row.title} originalName={row.original_name} />
            <Text type="secondary">ID {row.item_id}</Text>
          </Space>
        ),
      },
      { title: '范围', dataIndex: 'scope', key: 'scope', width: 110, render: (v: string) => scopeTag(v) },
      { title: '正文摘要', dataIndex: 'content_preview', key: 'content_preview', ellipsis: true },
    ],
    []
  )

  const fileDupGroups = res.file_duplicate_groups ?? 0
  const contentDupGroups = res.content_duplicate_groups ?? 0
  const hasDup = fileDupGroups > 0 || contentDupGroups > 0

  return (
    <>
      <div className="dedup-stats">
        <Card size="small">
          <Statistic title="扫描条目" value={res.scanned_items} />
        </Card>
        <Card size="small">
          <Statistic title="扫描附件" value={res.scanned_files} />
        </Card>
        <Card size="small">
          <Statistic title="附件重复组" value={fileDupGroups} />
        </Card>
        <Card size="small">
          <Statistic title="正文重复组" value={contentDupGroups} />
        </Card>
        <Card size="small">
          <Statistic title="耗时" value={res.response_ms} suffix="ms" />
        </Card>
      </div>

      {missingFiles > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={`有 ${missingFiles} 个附件记录在库中但磁盘文件缺失，已跳过。`}
        />
      ) : null}

      {!hasDup ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="未发现重复项，知识库附件与正文均为唯一。" />
      ) : null}

      {fileGroups.length > 0 ? (
        <section className="dedup-section">
          <div className="dedup-section-head">
            <CopyOutlined />
            <Text strong>附件重复（{fileDupGroups} 组）</Text>
          </div>
          <Collapse
            accordion
            items={fileGroups.map((g, idx) => ({
              key: String(idx),
              label: (
                <Space wrap>
                  <Tag color="orange">{g.count} 个相同文件</Tag>
                  <Text>{formatBytes(g.size)}</Text>
                  <Text type="secondary" copyable={{ text: g.hash }}>
                    {g.hash.slice(0, 12)}…
                  </Text>
                </Space>
              ),
              children: (
                <Table
                  rowKey={(r) => `${r.item_id}-${r.stored_name}`}
                  size="small"
                  pagination={false}
                  columns={fileColumns}
                  dataSource={g.files}
                />
              ),
            }))}
          />
        </section>
      ) : null}

      {contentGroups.length > 0 ? (
        <section className="dedup-section">
          <div className="dedup-section-head">
            <CopyOutlined />
            <Text strong>正文重复（{contentDupGroups} 组）</Text>
          </div>
          <Collapse
            accordion
            items={contentGroups.map((g, idx) => ({
              key: String(idx),
              label: (
                <Space wrap>
                  <Tag color="purple">{g.count} 条相同正文</Tag>
                  <Text type="secondary" copyable={{ text: g.hash }}>
                    {g.hash.slice(0, 12)}…
                  </Text>
                </Space>
              ),
              children: (
                <Table
                  rowKey={(r) => String(r.item_id)}
                  size="small"
                  pagination={false}
                  columns={contentColumns}
                  dataSource={g.items}
                />
              ),
            }))}
          />
        </section>
      ) : null}
    </>
  )
}

function ReportProgressCard({ meta }: { meta: DedupReportMeta }) {
  return (
    <Card className="dedup-toolbar" bordered={false}>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Space wrap>
          {statusTag(meta.status)}
          <Text>{meta.status_message}</Text>
        </Space>
        <Progress
          percent={meta.progress}
          status={meta.status === 'failed' ? 'exception' : meta.status === 'completed' ? 'success' : 'active'}
        />
        <Text type="secondary">报告 #{meta.id} · 完成后可在「查重报告」页再次打开</Text>
      </Space>
    </Card>
  )
}

function usePollDedupReport(reportId: number | null) {
  const [meta, setMeta] = useState<DedupReportMeta | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!reportId) {
      setMeta(null)
      return
    }
    abortRef.current?.abort()
    const ac = new AbortController()
    abortRef.current = ac
    let timer: ReturnType<typeof setTimeout> | undefined

    const tick = async () => {
      if (ac.signal.aborted) return
      try {
        const r = await api.get<DedupReportMeta>(`/public/dedup/reports/${reportId}`, {
          signal: ac.signal,
          params: { include_matches: false },
        })
        setMeta(r.data)
        if (r.data.status === 'pending' || r.data.status === 'running') {
          timer = setTimeout(tick, POLL_MS)
        }
      } catch (e) {
        if (!ac.signal.aborted) {
          message.error(e instanceof Error ? e.message : '获取报告状态失败')
        }
      }
    }
    tick()

    return () => {
      ac.abort()
      if (timer) clearTimeout(timer)
    }
  }, [reportId])

  return meta
}

type SourceMode = 'library' | 'upload'

function useDedupReportDrawer() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [viewReportId, setViewReportId] = useState<number | null>(null)
  const displayDetail = usePollDedupReport(drawerOpen ? viewReportId : null)

  const openReport = useCallback((id: number) => {
    setViewReportId(id)
    setDrawerOpen(true)
  }, [])

  const drawer = (
    <Drawer
      title={
        displayDetail ? (
          <Space direction="vertical" size={2}>
            <span>查重报告 #{displayDetail.id}</span>
            {displayDetail.query_title ? (
              <span style={{ fontWeight: 400, fontSize: 13 }}>{renderReportQueryTitle(displayDetail)}</span>
            ) : null}
          </Space>
        ) : (
          '查重报告'
        )
      }
      size={Math.min(960, window.innerWidth - 48)}
      open={drawerOpen}
      onClose={() => {
        setDrawerOpen(false)
        setViewReportId(null)
      }}
      destroyOnClose
    >
      {!displayDetail ? <PageLoading /> : null}
      {displayDetail && (displayDetail.status === 'pending' || displayDetail.status === 'running') ? (
        <ReportProgressCard meta={displayDetail} />
      ) : null}
      {displayDetail?.status === 'failed' ? (
        <Alert type="error" showIcon title={displayDetail.error_message || '任务失败'} />
      ) : null}
      {displayDetail?.status === 'completed' && displayDetail.result ? (
        <DedupReportDetailBody detail={displayDetail} />
      ) : null}
    </Drawer>
  )

  return { openReport, drawer }
}

function SimilarityDedupPanel({ onOpenReport }: { onOpenReport: (id: number) => void }) {
  const [sourceMode, setSourceMode] = useState<SourceMode>('library')
  const [corpusScope, setCorpusScope] = useState<ScopeOpt>('all')
  const [itemScope, setItemScope] = useState<ScopeOpt>('all')
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [selectedItem, setSelectedItem] = useState<{
    id: number
    title: string
    scope: string
    original_name?: string
  } | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const pickerPag = useServerPagination()
  const [pickerTotal, setPickerTotal] = useState(0)
  const [pickerItems, setPickerItems] = useState<DedupItem[]>([])
  const [pickerQ, setPickerQ] = useState('')
  const [pickerLoading, setPickerLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [activeReportId, setActiveReportId] = useState<number | null>(null)
  const pollMeta = usePollDedupReport(activeReportId)

  const loadPickerItems = useCallback(
    (q?: string) => {
      setPickerLoading(true)
      api
        .get<{ items: DedupItem[]; total: number }>('/public/dedup/items', {
          params: {
            scope: itemScope,
            page: pickerPag.page,
            page_size: pickerPag.pageSize,
            q: q?.trim() || undefined,
          },
        })
        .then((r) => {
          setPickerItems(r.data.items)
          setPickerTotal(r.data.total)
        })
        .catch((e) => message.error(e.message))
        .finally(() => setPickerLoading(false))
    },
    [itemScope, pickerPag.page, pickerPag.pageSize],
  )

  useEffect(() => {
    if (!pickerOpen) return
    loadPickerItems(pickerQ)
  }, [pickerOpen, pickerPag.page, pickerPag.pageSize, pickerQ, loadPickerItems])

  const openPicker = () => {
    setPickerOpen(true)
    pickerPag.resetPage()
  }

  const onCheck = () => {
    if (sourceMode === 'library') {
      if (!selectedItem) {
        message.warning('请先选择待查文档')
        return
      }
      setSubmitting(true)
      setActiveReportId(null)
      api
        .post<{ report_id: number; message: string }>('/public/dedup/check', {
          item_id: selectedItem.id,
          scope: corpusScope,
        })
        .then((r) => {
          message.success(r.data.message || '已提交查重')
          setActiveReportId(r.data.report_id)
        })
        .catch((e) => message.error(e.message))
        .finally(() => setSubmitting(false))
      return
    }

    if (!uploadFile) {
      message.warning('请先上传本地文档')
      return
    }
    setSubmitting(true)
    setActiveReportId(null)
    const fd = new FormData()
    fd.append('file', uploadFile)
    fd.append('scope', corpusScope)
    api
      .post<{ report_id: number; message: string }>('/public/dedup/check-upload', fd, {
        timeout: 0,
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => {
        message.success(r.data.message || '已提交上传查重')
        setActiveReportId(r.data.report_id)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setSubmitting(false))
  }

  const docReady = sourceMode === 'library' ? !!selectedItem : !!uploadFile
  const similarityResult =
    pollMeta?.status === 'completed' && pollMeta.report_type === 'similarity' && pollMeta.result
      ? (pollMeta.result as SimilarityResult)
      : null

  const pickItem = (row: DedupItem) => {
    setSelectedItem({
      id: row.id,
      title: row.title,
      scope: row.scope,
      original_name: row.original_name,
    })
    setPickerOpen(false)
  }

  return (
    <>
      <DedupHero
        icon={<FileSearchOutlined />}
        title="选择待查文档，比对知识库相似内容"
        description="统计重复率并标红重复片段；同时检测与库内完全相同的附件或正文"
      />

      {!activeReportId ? (
        <div className="dedup-setup">
          <DedupSteps
            steps={[
              '选择或上传待查 PDF / Word',
              '选择要比对的知识库范围',
              '点击「开始相似度查重」，结果将显示在下方',
            ]}
          />
          <div className="dedup-options-row dedup-options-row--setup">
            <div className="dedup-inline-option">
              <Text type="secondary">文档筛选范围</Text>
              <Select<ScopeOpt>
                value={itemScope}
                disabled={sourceMode !== 'library'}
                onChange={(v) => {
                  setItemScope(v)
                  setSelectedItem(null)
                }}
                options={[
                  { value: 'all', label: '全部' },
                  { value: 'enterprise', label: '企业知识库' },
                  { value: 'personal', label: '个人知识库' },
                ]}
              />
            </div>
            <div className="dedup-inline-option">
              <Text type="secondary">对比语料范围</Text>
              <Select<ScopeOpt>
                value={corpusScope}
                onChange={setCorpusScope}
                options={[
                  { value: 'all', label: '全部知识库' },
                  { value: 'enterprise', label: '企业知识库' },
                  { value: 'personal', label: '个人知识库' },
                ]}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className="dedup-workspace">
        <div className="dedup-workspace-head">
          <span className="dedup-workspace-label">待查文档</span>
          <DedupSourceTabs
            value={sourceMode}
            options={[
              { value: 'library', label: '知识库' },
              { value: 'upload', label: '本地上传' },
            ]}
            onChange={(v) => {
              setSourceMode(v)
              setSelectedItem(null)
              setUploadFile(null)
            }}
          />
        </div>
        <div className="dedup-workspace-body">
          <div className="dedup-doc-slot">
            {sourceMode === 'library' ? (
              <DedupLibrarySlot selected={selectedItem} onBrowse={openPicker} />
            ) : (
              <DedupFileSlot file={uploadFile} onFileChange={setUploadFile} />
            )}
          </div>
        </div>
      </div>

      {!activeReportId ? (
        <DedupActionBar
          ready={docReady}
          loading={submitting}
          label="开始相似度查重"
          icon={<FileSearchOutlined />}
          onSubmit={onCheck}
        />
      ) : null}

      <DedupResultSection reportId={activeReportId} pollMeta={pollMeta}>
        {similarityResult && activeReportId ? (
          <>
            <div className="dedup-result-toolbar">
              <Text type="secondary">报告 #{activeReportId}</Text>
              <Button type="link" size="small" onClick={() => onOpenReport(activeReportId)}>
                抽屉中查看
              </Button>
            </div>
            <SimilaritySummaryCard
              res={similarityResult}
              onOpenFull={() => onOpenReport(activeReportId)}
            />
          </>
        ) : null}
      </DedupResultSection>

      <Drawer
        title="选择待查文档"
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        width={Math.min(640, window.innerWidth - 48)}
        destroyOnClose
      >
        <Input.Search
          placeholder="搜索标题 / 标签"
          allowClear
          style={{ marginBottom: 12 }}
          value={pickerQ}
          onChange={(e) => setPickerQ(e.target.value)}
          onSearch={(v) => {
            pickerPag.resetPage()
            setPickerQ(v)
          }}
        />
        <Table
          rowKey="id"
          size="small"
          loading={pickerLoading}
          dataSource={pickerItems}
          pagination={tablePagination(
            pickerPag.page,
            pickerTotal,
            pickerPag.handlePaginationChange,
            pickerPag.paginationOptions,
          )}
          onRow={(row) => ({
            onClick: () => pickItem(row),
            style: { cursor: 'pointer' },
          })}
          columns={[
            { title: '标题', dataIndex: 'title', ellipsis: true },
            {
              title: '文件名',
              dataIndex: 'original_name',
              ellipsis: true,
              render: (v: string | undefined) => v || '—',
            },
            {
              title: '范围',
              dataIndex: 'scope',
              width: 100,
              render: (v: string) => scopeTag(v),
            },
            {
              title: '',
              width: 72,
              render: (_: unknown, row: DedupItem) => (
                <Button type="link" size="small" onClick={() => pickItem(row)}>
                  选择
                </Button>
              ),
            },
          ]}
        />
      </Drawer>
    </>
  )
}

type ExactSourceMode = 'scan' | 'upload'

function ExactDedupPanel({ onOpenReport }: { onOpenReport: (id: number) => void }) {
  const [sourceMode, setSourceMode] = useState<ExactSourceMode>('scan')
  const [scope, setScope] = useState<ScopeOpt>('all')
  const [mode, setMode] = useState<ModeOpt>('both')
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [activeReportId, setActiveReportId] = useState<number | null>(null)
  const pollMeta = usePollDedupReport(activeReportId)

  const onScan = () => {
    if (sourceMode === 'upload') {
      if (!uploadFile) {
        message.warning('请先上传本地文档')
        return
      }
      setSubmitting(true)
      setActiveReportId(null)
      const fd = new FormData()
      fd.append('file', uploadFile)
      fd.append('scope', scope)
      fd.append('mode', mode)
      api
        .post<{ report_id: number; message: string }>('/public/dedup/scan-upload', fd, {
          timeout: 0,
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => {
          message.success(r.data.message || '已提交上传精确查重')
          setActiveReportId(r.data.report_id)
        })
        .catch((e) => message.error(e.message))
        .finally(() => setSubmitting(false))
      return
    }

    setSubmitting(true)
    setActiveReportId(null)
    api
      .post<{ report_id: number; message: string }>('/public/dedup/scan', { scope, mode })
      .then((r) => {
        message.success(r.data.message || '已提交扫描')
        setActiveReportId(r.data.report_id)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setSubmitting(false))
  }

  const exactResult =
    pollMeta?.status === 'completed' && pollMeta.report_type === 'exact_scan' && pollMeta.result
      ? pollMeta.result
      : null
  const uploadResult = exactResult && isExactUploadResult(exactResult) ? exactResult : null
  const scanResult =
    exactResult && !isExactUploadResult(exactResult) ? (exactResult as ScanResult) : null

  const scanReady = sourceMode === 'scan' || !!uploadFile

  return (
    <>
      <DedupHero
        icon={<ScanOutlined />}
        title={sourceMode === 'scan' ? '扫描知识库中的完全重复项' : '上传文档检测完全重复'}
        description="按附件二进制或正文哈希比对，找出与库内完全相同的 PDF / Word 文档"
      />

      {!activeReportId ? (
        <div className="dedup-setup">
          <DedupSteps
            steps={
              sourceMode === 'scan'
                ? ['选择扫描范围与比对维度', '点击开始，系统将列出完全相同的附件或正文']
                : ['上传待查 PDF / Word', '选择对比语料范围与比对维度', '点击开始精确查重，结果将显示在下方']
            }
          />
          <div className="dedup-options-row dedup-options-row--setup">
            <div className="dedup-inline-option">
              <Text type="secondary">{sourceMode === 'upload' ? '对比语料范围' : '扫描范围'}</Text>
              <Select<ScopeOpt>
                value={scope}
                onChange={setScope}
                options={[
                  { value: 'all', label: '全部知识库' },
                  { value: 'enterprise', label: '企业知识库' },
                  { value: 'personal', label: '个人知识库' },
                ]}
              />
            </div>
            <div className="dedup-inline-option">
              <Text type="secondary">比对维度</Text>
              <Select<ModeOpt>
                value={mode}
                onChange={setMode}
                options={[
                  { value: 'both', label: '附件 + 正文' },
                  { value: 'file', label: '仅附件' },
                  { value: 'content', label: '仅正文' },
                ]}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className="dedup-workspace">
        <div className="dedup-workspace-head">
          <span className="dedup-workspace-label">{sourceMode === 'scan' ? '扫描模式' : '待查文档'}</span>
          <DedupSourceTabs
            value={sourceMode}
            options={[
              { value: 'scan', label: '全库扫描' },
              { value: 'upload', label: '单文件比对' },
            ]}
            onChange={(v) => {
              setSourceMode(v)
              setUploadFile(null)
            }}
          />
        </div>
        <div className="dedup-workspace-body">
          <div className="dedup-doc-slot">
            {sourceMode === 'scan' ? (
              <Alert
                type="info"
                showIcon
                className="dedup-scan-alert"
                message="全库扫描"
                description="在选定范围内批量查找附件或正文完全相同的文档，无需选择待查文件。"
              />
            ) : (
              <DedupFileSlot file={uploadFile} onFileChange={setUploadFile} />
            )}
          </div>
        </div>
      </div>

      {!activeReportId ? (
        <DedupActionBar
          ready={scanReady}
          loading={submitting}
          label={sourceMode === 'upload' ? '开始精确查重' : '开始全库扫描'}
          icon={<ScanOutlined />}
          onSubmit={onScan}
        />
      ) : null}

      <DedupResultSection reportId={activeReportId} pollMeta={pollMeta}>
        {activeReportId ? (
          <>
            <div className="dedup-result-toolbar">
              <Text type="secondary">报告 #{activeReportId}</Text>
              <Button type="link" size="small" onClick={() => onOpenReport(activeReportId)}>
                抽屉中查看
              </Button>
            </div>
            {uploadResult ? <ExactUploadReportBody res={uploadResult} /> : null}
            {scanResult ? <ExactScanReportBody res={scanResult} /> : null}
          </>
        ) : null}
      </DedupResultSection>
    </>
  )
}

type ReportTypeFilter = 'all' | 'similarity' | 'exact_scan'

function DedupReportsPanel({ onOpenReport }: { onOpenReport: (id: number) => void }) {
  const { page, pageSize, handlePaginationChange, paginationOptions, resetPage } = useServerPagination()
  const [total, setTotal] = useState(0)
  const [items, setItems] = useState<DedupReportMeta[]>([])
  const [loading, setLoading] = useState(false)
  const [typeFilter, setTypeFilter] = useState<ReportTypeFilter>('all')

  const loadList = useCallback(() => {
    setLoading(true)
    const params: Record<string, string | number> = { page, page_size: pageSize }
    if (typeFilter !== 'all') {
      params.report_type = typeFilter
    }
    api
      .get<{ items: DedupReportMeta[]; total: number }>('/public/dedup/reports', { params })
      .then((r) => {
        setItems(r.data.items)
        setTotal(r.data.total)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setLoading(false))
  }, [page, pageSize, typeFilter])

  useEffect(() => {
    loadList()
  }, [loadList])

  useEffect(() => {
    resetPage()
  }, [typeFilter, resetPage])

  const removeReport = useCallback(
    (id: number) => {
      api
        .delete(`/public/dedup/reports/${id}`)
        .then(() => {
          message.success('已删除查重记录')
          loadList()
        })
        .catch((e) => message.error(e.message))
    },
    [loadList],
  )

  const columns = useMemo(
    () => [
      {
        title: '类型',
        dataIndex: 'report_type',
        width: 110,
        render: (v: string) => {
          const color =
            v === 'similarity' ? 'blue' : v === 'exact_scan' ? 'orange' : v === 'dedup_reindex' ? 'default' : 'default'
          return <Tag color={color}>{reportTypeLabel(v)}</Tag>
        },
      },
      {
        title: '文档 / 任务',
        key: 'query',
        ellipsis: true,
        render: (_: unknown, row: DedupReportMeta) => renderReportQueryTitle(row),
      },
      {
        title: '结果摘要',
        key: 'summary',
        width: 160,
        render: (_: unknown, row: DedupReportMeta) => {
          if (row.status !== 'completed') return '—'
          if (row.report_type === 'similarity' && row.duplicate_rate != null) {
            return `${row.duplicate_rate.toFixed(2)}%`
          }
          const s = row.summary
          if (s?.file_duplicate_count != null || s?.content_duplicate_count != null) {
            return `附件 ${s.file_duplicate_count ?? 0} 篇 / 正文 ${s.content_duplicate_count ?? 0} 篇`
          }
          if (s?.file_duplicate_groups != null || s?.content_duplicate_groups != null) {
            return `附件 ${s.file_duplicate_groups ?? 0} 组 / 正文 ${s.content_duplicate_groups ?? 0} 组`
          }
          if (row.report_type === 'dedup_reindex') {
            return `MySQL ${s?.mysql_indexed ?? 0} / ES ${s?.es_fingerprints ?? 0}`
          }
          return '—'
        },
      },
      {
        title: '状态',
        dataIndex: 'status',
        width: 100,
        render: (v: string) => statusTag(v),
      },
      {
        title: '创建时间',
        dataIndex: 'created_at',
        width: 170,
        render: (v: string | null) => formatDate(v),
      },
      {
        title: '操作',
        key: 'action',
        width: 96,
        align: 'center' as const,
        render: (_: unknown, row: DedupReportMeta) => (
          <div className="dedup-report-actions" onClick={(e) => e.stopPropagation()}>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              title="查看"
              onClick={() => onOpenReport(row.id)}
            />
            <Popconfirm
              title="删除该查重记录？"
              description="删除后不可恢复"
              okText="删除"
              cancelText="取消"
              onConfirm={() => removeReport(row.id)}
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />} title="删除" />
            </Popconfirm>
          </div>
        ),
      },
    ],
    [onOpenReport, removeReport]
  )

  return (
    <>
      <div className="dedup-reports-toolbar">
        <Text type="secondary">相似查重与精确查重记录均在此查看，点击行打开详情</Text>
        <Select<ReportTypeFilter>
          value={typeFilter}
          onChange={setTypeFilter}
          style={{ width: 140 }}
          options={[
            { value: 'all', label: '全部类型' },
            { value: 'similarity', label: '相似查重' },
            { value: 'exact_scan', label: '精确查重' },
          ]}
        />
      </div>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={items}
        pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
        onRow={(row) => ({
          onClick: () => onOpenReport(row.id),
          style: { cursor: 'pointer' },
        })}
      />
    </>
  )
}

export default function FileDedup() {
  const [activeTab, setActiveTab] = useState('similarity')
  const { openReport, drawer } = useDedupReportDrawer()

  return (
    <div className="dedup-page public-page-scroll">
      <PageHeader
        level={2}
        title="文件查重"
        description="待查文档与比对语料均仅支持 PDF、Word。相似度查重统计重复率；精确查重找出完全相同的附件或正文。"
      />

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'similarity',
            label: '相似度查重',
            children: <SimilarityDedupPanel onOpenReport={openReport} />,
          },
          {
            key: 'exact',
            label: '精确查重',
            children: <ExactDedupPanel onOpenReport={openReport} />,
          },
          {
            key: 'reports',
            label: '查重报告',
            children: <DedupReportsPanel onOpenReport={openReport} />,
          },
        ]}
      />

      {drawer}
    </div>
  )
}
